import React, { useRef, useMemo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTheme } from '../hooks/useTheme';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarElement,
  BarController,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { buildCalendarLink, buildDiagonalLink, buildVerticalDebitLink } from '../utils/tradier';
import { getApiBaseUrl } from '../apiConfig';
import { fetchGexProfileCached } from '../services/api';

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarElement,
  BarController,
  zoomPlugin
);

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('sv-SE');
};

const formatNum = (v) => {
  if (v === null || v === undefined) return 'N/A';
  return Math.round(v).toLocaleString('en-US');
};

const readCssVar = (name, fallback) => {
  try {
    const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return val || fallback;
  } catch {
    return fallback;
  }
};

// ── Crosshair plugin (thin vertical + horizontal line following cursor) ──────
const crosshairPlugin = {
  id: 'crosshairPlugin',
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const meta = chart._crosshairMeta;
    if (!meta || meta.x == null || meta.y == null) return;
    const { top, bottom, left, right } = chartArea;

    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const lineColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)';
    const textBg = isDark ? 'rgba(22,28,36,0.9)' : 'rgba(255,255,255,0.92)';
    const textColor = isDark ? '#e6edf3' : '#1f2937';

    ctx.save();
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = lineColor;

    // Vertical line
    ctx.beginPath();
    ctx.moveTo(meta.x, top);
    ctx.lineTo(meta.x, bottom);
    ctx.stroke();

    // Horizontal line
    ctx.beginPath();
    ctx.moveTo(left, meta.y);
    ctx.lineTo(right, meta.y);
    ctx.stroke();

    ctx.setLineDash([]);

    // X-axis label
    const xVal = scales.x.getValueForPixel(meta.x);
    if (xVal != null) {
      const label = Math.round(xVal).toLocaleString();
      ctx.font = '11px Inter, sans-serif';
      const w = ctx.measureText(label).width + 10;
      const h = 18;
      const xPos = meta.x - w / 2;
      ctx.fillStyle = textBg;
      ctx.fillRect(xPos, bottom + 2, w, h);
      ctx.strokeStyle = lineColor;
      ctx.strokeRect(xPos, bottom + 2, w, h);
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, meta.x, bottom + 2 + h / 2);
    }

    // Y-axis label
    const yVal = scales.y.getValueForPixel(meta.y);
    if (yVal != null) {
      const label = Math.round(yVal).toLocaleString();
      ctx.font = '11px Inter, sans-serif';
      const w = ctx.measureText(label).width + 10;
      const h = 18;
      ctx.fillStyle = textBg;
      ctx.fillRect(left - w - 4, meta.y - h / 2, w, h);
      ctx.strokeStyle = lineColor;
      ctx.strokeRect(left - w - 4, meta.y - h / 2, w, h);
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, left - w / 2 - 4, meta.y);
    }

    ctx.restore();
  },
};

ChartJS.register(crosshairPlugin);

// ── Annotation-drawing plugin (reference lines, labels) ─────────────────────
const annotationDrawPlugin = {
  id: 'annotationDrawPlugin',
  afterDatasetsDraw(chart) {
    const annotations = chart.options?.plugins?.annotationDrawPlugin?.annotations;
    if (!annotations || !annotations.length) return;
    const { ctx, chartArea, scales } = chart;
    const { top, bottom, left, right } = chartArea;

    ctx.save();
    annotations.forEach((a) => {
      const { axis, value, color, dash, width, label, labelPosition, labelColor, labelFontSize } = a;
      if (value == null) return;

      if (axis === 'x') {
        const px = scales.x.getPixelForValue(value);
        if (px < left || px > right) return;
        ctx.beginPath();
        ctx.setLineDash(dash || []);
        ctx.lineWidth = width || 1;
        ctx.strokeStyle = color || '#999';
        ctx.moveTo(px, top);
        ctx.lineTo(px, bottom);
        ctx.stroke();
        if (label) {
          ctx.setLineDash([]);
          ctx.font = `${labelFontSize || 11}px Inter, sans-serif`;
          ctx.fillStyle = labelColor || color || '#999';
          ctx.textAlign = 'center';
          if (labelPosition === 'bottom') {
            ctx.textBaseline = 'top';
            ctx.fillText(label, px, bottom + 6);
          } else if (labelPosition === 'insideBottom') {
            ctx.textBaseline = 'bottom';
            ctx.fillText(label, px, bottom - 6);
          } else {
            ctx.textBaseline = 'top';
            ctx.fillText(label, px, top + 6);
          }
        }
      } else if (axis === 'y') {
        const py = scales.y.getPixelForValue(value);
        if (py < top || py > bottom) return;
        ctx.beginPath();
        ctx.setLineDash(dash || []);
        ctx.lineWidth = width || 1;
        ctx.strokeStyle = color || '#999';
        ctx.moveTo(left, py);
        ctx.lineTo(right, py);
        ctx.stroke();
        if (label) {
          ctx.setLineDash([]);
          ctx.font = `${labelFontSize || 11}px Inter, sans-serif`;
          ctx.fillStyle = labelColor || color || '#999';
          ctx.textAlign = labelPosition === 'right' ? 'right' : 'left';
          ctx.textBaseline = 'bottom';
          const xPos = labelPosition === 'right' ? right - 4 : left + 4;
          ctx.fillText(label, xPos, py - 4);
        }
      }
    });
    ctx.setLineDash([]);
    ctx.restore();
  },
};

ChartJS.register(annotationDrawPlugin);

// ── ActualChart ─────────────────────────────────────────────────────────────

const ActualChart = ({ selectedCombination }) => {
  const chartRef = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [gexProfile, setGexProfile] = useState(null);

  const {
    risk_graph,
    critical_points,
    min_pl_death_valley,
    max_pl,
    calendar_spread,
    diagonal_spread,
    vertical_spread,
  } = selectedCombination;

  // ── Theme (observed via hook so we re-render on switch) ──
  const theme = useTheme();
  const isDark = theme === 'dark';
  const themeColors = useMemo(() => {
    const colorAccent = readCssVar('--accent', '#5b9bff');
    const colorPositive = readCssVar('--positive', '#22c55e');
    const colorText = readCssVar('--text', isDark ? '#e6edf3' : '#1f2937');
    const colorMuted = readCssVar('--muted', '#6b7280');
    const colorBorder = readCssVar('--border', isDark ? '#2a313c' : '#e5e7eb');
    return {
      colorAccent,
      colorPositive,
      colorText,
      colorMuted,
      colorBorder,
      gridColor: isDark ? 'rgba(255,255,255,0.06)' : '#e8e8e8',
      expiryColor: isDark ? colorAccent : '#6366f1',
      t0Color: isDark ? colorPositive : '#22c55e',
      zeroColor: isDark ? 'rgba(255,255,255,0.18)' : '#cccccc',
      breakEvenColor: isDark ? colorPositive : '#16a34a',
      strikeColor: isDark ? 'rgba(255,255,255,0.12)' : '#d4d4d4',
    };
  }, [isDark]);
  const {
    colorAccent, colorText, colorMuted, colorBorder,
    gridColor, expiryColor, t0Color, zeroColor, breakEvenColor, strikeColor,
  } = themeColors;

  // ── Data ──
  const firstExpirationDate = calendar_spread?.legs?.[0]?.expiration;
  const expiryLabel = firstExpirationDate ? `P/L @ ${formatDate(firstExpirationDate)}` : 'P/L at First Expiry';
  const t0Label = `P/L @ ${formatDate(new Date().toISOString().split('T')[0])}`;

  const chartData = useMemo(() => {
    return risk_graph.prices.map((price, i) => ({
      x: parseFloat(price.toFixed(2)),
      y: parseFloat((risk_graph.pl[i] * 100).toFixed(2)),
    }));
  }, [risk_graph.prices, risk_graph.pl]);

  const t0Data = useMemo(() => {
    if (!risk_graph.pl_t0) return null;
    return risk_graph.prices.map((price, i) => ({
      x: parseFloat(price.toFixed(2)),
      y: parseFloat((risk_graph.pl_t0[i] * 100).toFixed(2)),
    }));
  }, [risk_graph.prices, risk_graph.pl_t0]);

  // ── X domain (trim tails) ──
  const xDomain = useMemo(() => {
    if (chartData.length <= 10) return { min: chartData[0]?.x, max: chartData[chartData.length - 1]?.x };
    const leftPad = Math.max(1, Math.floor(chartData.length * 0.03));
    const rightPad = Math.max(1, Math.floor(chartData.length * 0.15));
    let min = chartData[leftPad].x;
    let max = chartData[Math.max(leftPad, chartData.length - 1 - rightPad)].x;
    if (min >= max) {
      min = chartData[0].x;
      max = chartData[chartData.length - 1].x;
    }
    return { min, max };
  }, [chartData]);

  // ── Fetch GEX Profile (Cached) — layout effect so gex.php runs before fusion_history.php ──
  useLayoutEffect(() => {
    if (!firstExpirationDate) return;
    const ticker = calendar_spread?.legs?.[0]?.symbol?.substring(0, 3) === 'SPX' ? 'SPX' : 'SPX'; // Simplification for now
    const expiry = firstExpirationDate.split('T')[0];
    
    fetchGexProfileCached(ticker, expiry)
      .then(data => {
        if (data.strikes && data.strikes.length > 0) setGexProfile(data);
        else setGexProfile(null);
      })
      .catch(err => {
        console.error("GEX fetch error:", err);
        setGexProfile(null);
      });
  }, [firstExpirationDate, calendar_spread]);

  // ── Y domain ──
  const yDomain = useMemo(() => {
    const allPl = chartData.map((d) => d.y);
    if (t0Data) allPl.push(...t0Data.map((d) => d.y));
    if (min_pl_death_valley != null) allPl.push(min_pl_death_valley * 100);
    if (max_pl != null) allPl.push(max_pl * 100);
    let min = Math.min(...allPl);
    let max = Math.max(...allPl);
    const pad = Math.abs(max - min) * 0.08 || 60;
    return { min: min - pad, max: max + pad };
  }, [chartData, t0Data, min_pl_death_valley, max_pl]);

  // ── Breakevens, strikes ──
  const breakevens = useMemo(() => {
    if (!critical_points?.breakevens?.length) return [];
    return [...critical_points.breakevens].sort((a, b) => Number(a) - Number(b));
  }, [critical_points]);

  const firstExpiryStrikes = useMemo(() => {
    if (!firstExpirationDate) return [];
    const allLegs = [
      ...(calendar_spread?.legs || []),
      ...(diagonal_spread?.legs || []),
      ...(vertical_spread?.legs || []),
    ];
    const strikes = new Set();
    allLegs.forEach((leg) => {
      if (leg?.expiration === firstExpirationDate && leg?.strike != null) {
        strikes.add(Number(leg.strike));
      }
    });
    return [...strikes].sort((a, b) => a - b);
  }, [firstExpirationDate, calendar_spread, diagonal_spread, vertical_spread]);

  const currentStockPrice =
    calendar_spread?.legs?.[0]?.stockprice ||
    diagonal_spread?.legs?.[0]?.stockprice ||
    vertical_spread?.legs?.[0]?.stockprice;

  // ── Death Valley / Max P/L ──
  const dvPl = min_pl_death_valley != null ? min_pl_death_valley * 100 : null;
  const dvPrice = critical_points?.minima?.[0] != null ? Number(critical_points.minima[0]) : null;
  const maxPlVal = max_pl != null ? max_pl * 100 : null;
  const maxPlPrice = useMemo(() => {
    if (maxPlVal == null) return null;
    const target = parseFloat(maxPlVal.toFixed(2));
    const pt = chartData.find((d) => d.y === target);
    return pt ? pt.x : null;
  }, [chartData, maxPlVal]);

  // ── Build annotations array ──
  const annotations = useMemo(() => {
    const arr = [];

    // Zero line
    arr.push({ axis: 'y', value: 0, color: zeroColor, width: 2, dash: [] });

    // Breakevens
    breakevens.forEach((bp, i) => {
      const isLast = i === breakevens.length - 1;
      arr.push({
        axis: 'x',
        value: Number(bp),
        color: breakEvenColor,
        dash: [4, 4],
        label: isLast ? formatNum(Number(bp)) : null,
        labelPosition: 'top',
        labelColor: breakEvenColor,
        labelFontSize: 11,
      });
    });

    // Strikes
    firstExpiryStrikes.forEach((strike) => {
      arr.push({
        axis: 'x',
        value: strike,
        color: strikeColor,
        dash: [2, 3],
      });
    });

    // Current stock price
    if (currentStockPrice != null) {
      arr.push({
        axis: 'x',
        value: currentStockPrice,
        color: '#FF8C00',
        dash: [5, 4],
        label: formatNum(currentStockPrice),
        labelPosition: 'top',
        labelColor: '#FF8C00',
        labelFontSize: 10,
      });
    }

    // Death Valley
    if (dvPl != null && dvPrice != null) {
      arr.push({
        axis: 'x',
        value: dvPrice,
        color: '#ef4444',
        dash: [3, 3],
        label: `VoD: ${formatNum(dvPl)} @ ${formatNum(dvPrice)}`,
        labelPosition: 'insideBottom',
        labelColor: '#ef4444',
        labelFontSize: 11,
      });
    } else if (dvPl != null) {
      arr.push({
        axis: 'y',
        value: dvPl,
        color: '#ef4444',
        dash: [5, 4],
        label: `VoD: ${formatNum(dvPl)}`,
        labelPosition: 'left',
        labelColor: '#ef4444',
        labelFontSize: 11,
      });
    }

    // Max P/L
    if (maxPlVal != null && maxPlPrice != null) {
      arr.push({
        axis: 'x',
        value: maxPlPrice,
        color: colorAccent,
        dash: [3, 3],
        label: `Max: ${formatNum(maxPlVal)} @ ${formatNum(maxPlPrice)}`,
        labelPosition: 'top',
        labelColor: colorAccent,
        labelFontSize: 11,
      });
    } else if (maxPlVal != null) {
      arr.push({
        axis: 'y',
        value: maxPlVal,
        color: colorAccent,
        dash: [5, 4],
        label: `Max: ${formatNum(maxPlVal)}`,
        labelPosition: 'right',
        labelColor: colorAccent,
        labelFontSize: 11,
      });
    }

    return arr;
  }, [
    zeroColor, breakevens, breakEvenColor, firstExpiryStrikes, strikeColor,
    isDark, currentStockPrice, dvPl, dvPrice, maxPlVal, maxPlPrice, colorAccent,
  ]);

  // ── Chart.js datasets ──
  const data = useMemo(
    () => {
      const datasets = [
        {
          label: expiryLabel,
          data: chartData,
          borderColor: expiryColor,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: expiryColor,
          tension: 0.3,
          order: 1,
          yAxisID: 'y',
        },
      ];
      
      if (t0Data) {
        datasets.push({
          label: t0Label,
          data: t0Data,
          borderColor: t0Color,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: t0Color,
          tension: 0.3,
          borderDash: [6, 3],
          order: 2,
          yAxisID: 'y',
        });
      }
      
      if (gexProfile && gexProfile.strikes && gexProfile.values) {
        const gexData = gexProfile.strikes.map((strike, i) => ({
          x: strike,
          y: Math.abs(gexProfile.values[i]),
          rawY: gexProfile.values[i]
        }));
        datasets.push({
          type: 'bar',
          label: 'GEX Profile',
          data: gexData,
          backgroundColor: gexProfile.values.map((v) =>
            v >= 0 ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)'
          ),
          borderColor: gexProfile.values.map((v) =>
            v >= 0 ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)'
          ),
          borderWidth: 1,
          order: 3,
          yAxisID: 'yGex',
          barPercentage: 0.7,
          categoryPercentage: 0.9,
        });
      }
      
      return { datasets };
    },
    [chartData, t0Data, expiryLabel, t0Label, expiryColor, t0Color, gexProfile]
  );

  // ── GEX Scale Limits ──
  const maxGex = useMemo(() => {
    if (!gexProfile || !gexProfile.values || !gexProfile.strikes) return 1000;
    let maxVisible = 0;
    for (let i = 0; i < gexProfile.strikes.length; i++) {
      const strike = Number(gexProfile.strikes[i]);
      if (strike >= xDomain.min && strike <= xDomain.max) {
        const val = Math.abs(Number(gexProfile.values[i]));
        if (!isNaN(val) && val > maxVisible) {
          maxVisible = val;
        }
      }
    }
    return maxVisible > 0 ? maxVisible : 1000;
  }, [gexProfile, xDomain.min, xDomain.max]);

  const y1Scales = useMemo(() => {
    let pMin = Number(yDomain.min);
    let pMax = Number(yDomain.max);
    if (isNaN(pMin) || !isFinite(pMin)) pMin = -100;
    if (isNaN(pMax) || !isFinite(pMax)) pMax = 100;
    if (pMax === pMin) {
      pMin = -100;
      pMax = 100;
    }
    const P = Math.abs(pMin) / (pMax - pMin); // proportion of zero line from bottom
    
    let validMaxGex = Number(maxGex);
    if (isNaN(validMaxGex) || !isFinite(validMaxGex) || validMaxGex <= 0) validMaxGex = 1000;
    
    // Constant total range ensures the physical bar height is visually identical across all grid charts
    const TOTAL_RANGE = validMaxGex * 6; 
    
    let y1Max = TOTAL_RANGE * (1 - P);
    let y1Min = -TOTAL_RANGE * P;
    
    // Fallback if the P/L zero line is so high that the max bar would be clipped
    if (y1Max < validMaxGex) {
      y1Max = validMaxGex * 1.1;
      const safeDenom = Math.max(1 - P, 0.05);
      y1Min = -y1Max * (P / safeDenom);
    }
    
    if (isNaN(y1Min) || !isFinite(y1Min)) y1Min = -TOTAL_RANGE;
    if (isNaN(y1Max) || !isFinite(y1Max)) y1Max = TOTAL_RANGE;
    
    return { min: y1Min, max: y1Max };
  }, [yDomain.min, yDomain.max, maxGex]);

  // ── Chart.js options ──
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      layout: {
        padding: { top: 20 }
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      onHover: (event, _, chart) => {
        if (!event?.native) {
          chart._crosshairMeta = null;
          return;
        }
        const rect = chart.canvas.getBoundingClientRect();
        const x = event.native.clientX - rect.left;
        const y = event.native.clientY - rect.top;
        const { top, bottom, left, right } = chart.chartArea;
        if (x >= left && x <= right && y >= top && y <= bottom) {
          chart._crosshairMeta = { x, y };
        } else {
          chart._crosshairMeta = null;
        }
      },
      scales: {
        x: {
          type: 'linear',
          offset: false,
          min: xDomain.min,
          max: xDomain.max,
          title: { display: true, text: 'Underlying Price', color: colorMuted, font: { size: 12 } },
          grid: { color: gridColor, lineWidth: 0.5 },
          ticks: {
            color: colorText,
            font: { size: 11 },
            callback: (v) => Math.round(v).toLocaleString(),
            maxTicksLimit: 10,
          },
        },
        y: {
          type: 'linear',
          min: yDomain.min,
          max: yDomain.max,
          position: 'left',
          title: { display: true, text: 'Profit / Loss ($)', color: colorMuted, font: { size: 12 } },
          grid: { color: gridColor, lineWidth: 0.5 },
          ticks: {
            color: colorText,
            font: { size: 11 },
            callback: (v) => {
              const r = Math.round(v);
              if (Math.abs(r) >= 1000) return `${(r / 1000).toFixed(1)}K`;
              return r.toLocaleString();
            },
          },
        },
        yGex: {
          type: 'linear',
          position: 'right',
          display: true,
          grid: { drawOnChartArea: false },
          min: y1Scales.min,
          max: y1Scales.max,
          title: { display: true, text: 'GEX', color: colorMuted, font: { size: 12 } },
          ticks: {
            color: colorText,
            font: { size: 11 },
            callback: (v) => {
              const r = Math.round(v);
              if (Math.abs(r) >= 1000000) return `${(r / 1000000).toFixed(1)}M`;
              if (Math.abs(r) >= 1000) return `${(r / 1000).toFixed(1)}K`;
              return r.toLocaleString();
            },
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          align: 'start',
          labels: {
            color: colorText,
            font: { size: 11 },
            usePointStyle: true,
            pointStyle: 'line',
            padding: 14,
            boxWidth: 20,
          },
        },
        tooltip: {
          backgroundColor: isDark ? 'rgba(22,28,36,0.95)' : 'rgba(255,255,255,0.96)',
          titleColor: colorText,
          bodyColor: colorText,
          borderColor: colorBorder,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          bodyFont: { size: 13 },
          titleFont: { size: 12 },
          callbacks: {
            title: (items) => {
              if (!items.length) return '';
              const price = items[0].parsed?.x;
              return price != null ? `Price: ${Math.round(price).toLocaleString()}` : '';
            },
            label: (item) => {
              let v = item.parsed?.y;
              if (item.dataset.label === 'GEX Profile' && item.raw && item.raw.rawY !== undefined) {
                v = item.raw.rawY;
              }
              if (v == null) return '';
              return ` ${item.dataset.label}: ${formatNum(v)}`;
            },
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'x',
            onPanComplete: () => setIsZoomed(true),
          },
          zoom: {
            wheel: { enabled: true, speed: 0.08 },
            pinch: { enabled: true },
            mode: 'x',
            onZoomComplete: () => setIsZoomed(true),
          },
          limits: {
            x: {
              min: chartData.length > 0 ? chartData[0].x : undefined,
              max: chartData.length > 0 ? chartData[chartData.length - 1].x : undefined,
            },
          },
        },
        annotationDrawPlugin: {
          annotations,
        },
      },
    }),
    [
      xDomain, yDomain, colorText, colorMuted, colorBorder, gridColor, isDark,
      annotations, chartData, y1Scales
    ]
  );

  // ── Reset zoom ──
  const handleResetZoom = useCallback(() => {
    const chart = chartRef.current;
    if (chart) {
      chart.resetZoom();
      setIsZoomed(false);
    }
  }, []);

  // Clear crosshair on mouse leave
  useEffect(() => {
    const canvas = chartRef.current?.canvas;
    if (!canvas) return;
    const handleLeave = () => {
      const chart = chartRef.current;
      if (chart) {
        chart._crosshairMeta = null;
        chart.draw();
      }
    };
    canvas.addEventListener('mouseleave', handleLeave);
    return () => canvas.removeEventListener('mouseleave', handleLeave);
  }, []);

  // ── Tradier buttons ──
  const tradierLinks = {
    calendar: buildCalendarLink(selectedCombination),
    diagonal: buildDiagonalLink(selectedCombination),
    vertical: buildVerticalDebitLink(selectedCombination),
  };

  const btnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    border: `1px solid ${colorBorder}`,
    background: 'transparent',
    color: colorText,
    cursor: 'pointer',
    marginLeft: '6px',
    fontSize: '12px',
    fontWeight: 600,
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Tradier buttons */}
      <div style={{ position: 'absolute', top: 6, right: 12, zIndex: 5, display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: 8, fontSize: 11, color: colorMuted }}>Tradier</span>
        {['calendar', 'diagonal', 'vertical'].map((type) => (
          <button
            key={type}
            onClick={() => tradierLinks[type] && window.open(tradierLinks[type], '_blank')}
            disabled={!tradierLinks[type]}
            title={!tradierLinks[type] ? `${type} not available` : `Open ${type} ticket`}
            style={{
              ...btnStyle,
              cursor: tradierLinks[type] ? 'pointer' : 'not-allowed',
              opacity: tradierLinks[type] ? 1 : 0.4,
            }}
          >
            {type[0].toUpperCase()}
          </button>
        ))}
      </div>

      {/* Reset zoom badge */}
      {isZoomed && (
        <button
          onClick={handleResetZoom}
          style={{
            position: 'absolute',
            top: 38,
            right: 12,
            zIndex: 5,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 6,
            border: `1px solid ${colorBorder}`,
            background: isDark ? 'rgba(22,28,36,0.9)' : 'rgba(255,255,255,0.95)',
            color: colorAccent,
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          }}
        >
          ↩ Reset Zoom
        </button>
      )}

      <Line ref={chartRef} data={data} options={options} />
    </div>
  );
};

// ── Wrapper ────────────────────────────────────────────────────────────────

const RiskGraph = ({ selectedCombination }) => {
  if (!selectedCombination || !selectedCombination.risk_graph) {
    return <div className="no-graph">Select a combination to view its risk graph.</div>;
  }

  const { risk_graph } = selectedCombination;
  if (!risk_graph.prices || !risk_graph.pl || risk_graph.prices.length === 0) {
    return <div className="no-graph">Risk graph data is incomplete or unavailable.</div>;
  }

  return <ActualChart selectedCombination={selectedCombination} />;
};

export default RiskGraph;